import { useState } from 'react';
import { api } from './api.js';
import { toLocalInput, fromLocalInput, stamp, relativeToNow } from './format.js';

/**
 * The action tracker — an ICS 233 that keeps its own time.
 *
 * The column that earns this screen its place is "target". A list of outstanding actions
 * is a list; a list that tells you which of them have gone past the time they were wanted
 * by, without anybody maintaining that, is a tracker. The lateness is computed on the
 * server at read time, so it is right whether the page has been open for four seconds or
 * four hours — nothing here decides it.
 */

const FILTERS = [
  { key: 'open', label: 'Open' },
  { key: 'late', label: 'Late' },
  { key: 'all', label: 'All' },
];

const STATE_LABEL = { open: 'Open', done: 'Done', na: 'Stood down' };

export function Actions({ incidentId, actions, positions, strategies = [], canWrite, onChanged }) {
  const [filter, setFilter] = useState('open');
  const [editing, setEditing] = useState(null); // an action, or 'new'
  const [error, setError] = useState(null);

  const positionName = (id) => positions.find((p) => p.id === id)?.name ?? '';

  const shown = actions.filter((a) => (
    filter === 'all' ? true : filter === 'late' ? a.late : a.state === 'open'
  ));

  const counts = {
    open: actions.filter((a) => a.state === 'open').length,
    late: actions.filter((a) => a.late).length,
    all: actions.length,
  };

  async function setState(action, state) {
    setError(null);
    try {
      await api.updateAction(incidentId, action.id, { state });
      onChanged?.();
    } catch (err) {
      setError(err.status === 403 ? 'Your role cannot change actions.' : 'That change did not save.');
    }
  }

  return (
    <div className="card">
      <h2>
        Action tracker
        <span className="tabs">
          {FILTERS.map((f) => (
            <button key={f.key} className={`tab ${filter === f.key ? 'on' : ''}`}
                    onClick={() => setFilter(f.key)}>
              {f.label} <span className="n">{counts[f.key]}</span>
            </button>
          ))}
        </span>
        {canWrite && (
          <button className="hoBtn head" onClick={() => setEditing('new')}>Raise action</button>
        )}
      </h2>

      {error && <p className="err inline">{error}</p>}

      <div className="body">
        {shown.length === 0 && (
          <div className="empty">
            {actions.length === 0 ? 'No actions raised yet.'
              : filter === 'late' ? 'Nothing is past its target time.'
              : 'Nothing outstanding.'}
          </div>
        )}

        {shown.length > 0 && (
          <table className="grid">
            <thead>
              <tr>
                <th className="num">#</th>
                <th className="pcol">Pri</th>
                <th>Action</th>
                <th>Owner</th>
                <th>Position</th>
                <th>Target</th>
                <th className="right">{canWrite ? '' : 'Status'}</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((a) => (
                <tr key={a.id} className={`${a.late ? 'late' : ''} ${a.state !== 'open' ? 'closed' : ''}`}>
                  <td className="num">{a.seq}</td>
                  <td className="pcol"><span className={`pri p${a.priority}`}>P{a.priority}</span></td>
                  <td>
                    <button className="linkish" onClick={() => canWrite && setEditing(a)}
                            disabled={!canWrite} title={canWrite ? 'Edit this action' : undefined}>
                      {a.title}
                    </button>
                    {a.detail && <div className="sub">{a.detail}</div>}
                  </td>
                  <td>{a.owner_name || <span className="dimmed">unassigned</span>}</td>
                  <td className="dimmed">{positionName(a.position_id)}</td>
                  <td>
                    {a.due_at
                      ? <>
                          <div>{stamp(a.due_at)}</div>
                          {/* Only while it is open. "3h late" against a completed action
                              is noise — it was closed, and when it was closed is what
                              matters, not how far past the target the clock has since run. */}
                          {a.state === 'open' && (
                            <div className={`sub ${a.late ? 'bad' : ''}`}>{relativeToNow(a.due_at)}</div>
                          )}
                        </>
                      : <span className="dimmed">—</span>}
                  </td>
                  <td className="right">
                    {a.state === 'open'
                      ? (canWrite
                          ? <button className="hoBtn" onClick={() => setState(a, 'done')}>Complete</button>
                          : <span className="dimmed">Open</span>)
                      : (
                        <span className="closedTag">
                          {STATE_LABEL[a.state]}
                          {canWrite && (
                            <button className="linkbtn small" onClick={() => setState(a, 'open')}>reopen</button>
                          )}
                        </span>
                      )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <ActionEditor
          incidentId={incidentId}
          action={editing === 'new' ? null : editing}
          positions={positions}
          strategies={strategies}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged?.(); }}
        />
      )}
    </div>
  );
}

// ------------------------------------------------------------------ the editor

const blank = { title: '', detail: '', priority: 3, ownerName: '', positionId: '', dueAt: '', strategyId: '' };

function ActionEditor({ incidentId, action, positions, strategies = [], onClose, onSaved }) {
  const [form, setForm] = useState(() => (action ? {
    title: action.title, detail: action.detail, priority: action.priority,
    ownerName: action.owner_name, positionId: action.position_id ?? '',
    dueAt: toLocalInput(action.due_at),
    strategyId: action.strategy_id ?? '',
  } : blank));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.title.trim()) return;
    setBusy(true); setError(null);

    const body = {
      title: form.title.trim(),
      detail: form.detail.trim(),
      priority: Number(form.priority),
      ownerName: form.ownerName.trim(),
      positionId: form.positionId || null,
      dueAt: fromLocalInput(form.dueAt),
    };

    try {
      // The strategy link is a separate call on purpose: it is a change to what a strategy
      // is made of, not a column of the action, and the server checks a different
      // permission for it. Sent only when it actually moved, so an ordinary edit never
      // writes a "became a tactic" line into the log saying nothing happened.
      const wasLinked = action?.strategy_id ?? null;
      const nowLinked = form.strategyId || null;

      let id = action?.id;
      if (action) await api.updateAction(incidentId, action.id, body);
      else id = (await api.createAction(incidentId, body)).action.id;

      if (nowLinked !== wasLinked) await api.setTactic(incidentId, id, nowLinked);
      onSaved();
    } catch (err) {
      setError(err.status === 403 ? 'Your role cannot raise or change actions.'
        : err.detail || 'That did not save.');
      setBusy(false);
    }
  }

  async function standDown() {
    setBusy(true); setError(null);
    try {
      // Never deleted. An action raised in error is part of what happened, and standing it
      // down leaves it visible with a reason instead of removing the evidence it existed.
      await api.updateAction(incidentId, action.id, { state: 'na' });
      onSaved();
    } catch { setError('That did not save.'); setBusy(false); }
  }

  return (
    <div className="hoWrap" onClick={onClose}>
      <div className="ho" onClick={(e) => e.stopPropagation()}>
        <div className="hoHead">
          <div>
            <h3>{action ? `Action ${action.seq}` : 'Raise an action'}</h3>
            <p>{action ? 'Amendments are recorded in the incident log' : 'This will be numbered and logged'}</p>
          </div>
          <button className="hoX" onClick={onClose} aria-label="Close">×</button>
        </div>

        <form className="hoBody form" onSubmit={submit}>
          <label className="f">
            <span>What is to be done</span>
            <input value={form.title} onChange={set('title')} maxLength={300} required autoFocus
                   placeholder="Confirm the muster count against the POB list" />
          </label>

          <label className="f">
            <span>Detail (optional)</span>
            <textarea value={form.detail} onChange={set('detail')} rows={2} maxLength={4000} />
          </label>

          <div className="row3">
            <label className="f">
              <span>Priority</span>
              <select value={form.priority} onChange={set('priority')}>
                <option value={1}>P1 — immediate</option>
                <option value={2}>P2 — this period</option>
                <option value={3}>P3 — when able</option>
              </select>
            </label>
            <label className="f">
              <span>Owner</span>
              <input value={form.ownerName} onChange={set('ownerName')} maxLength={120}
                     placeholder="Name of the person it is given to" />
            </label>
          </div>

          <div className="row3">
            <label className="f">
              <span>Position</span>
              <select value={form.positionId} onChange={set('positionId')}>
                <option value="">Not tied to a position</option>
                {positions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="f">
              <span>Target time</span>
              {/* Local time. The conversion to an instant happens in format.js, once. */}
              <input type="datetime-local" value={form.dueAt} onChange={set('dueAt')} />
            </label>
          </div>

          {/* Only offered once somebody has written a strategy. Before that this is a
              control with one empty option, which invites the question "what goes here?"
              at the worst possible moment. */}
          {strategies.length > 0 && (
            <label className="f">
              <span>Delivers which strategy (optional)</span>
              <select value={form.strategyId} onChange={set('strategyId')}>
                <option value="">Not part of a strategy</option>
                {strategies.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.objective_seq}.{s.seq} — {s.title}
                  </option>
                ))}
              </select>
            </label>
          )}

          {error && <p className="err">{error}</p>}

          <div className="actions">
            {action && action.state !== 'na' && (
              <button type="button" className="linkbtn danger" onClick={standDown} disabled={busy}>
                Stand this action down
              </button>
            )}
            <span className="spacer" />
            <button type="button" className="ghost" onClick={onClose}>Cancel</button>
            <button className="btn narrow" type="submit" disabled={busy || !form.title.trim()}>
              {busy ? 'Saving…' : action ? 'Save changes' : 'Raise action'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
